import React, { useState, useEffect } from 'react';
import api from '../api';
import config from '../config';
import { FaEye, FaBullseye, FaUserTie, FaLinkedin, FaCheckCircle } from 'react-icons/fa';

const About = () => {
  // --- 1. State for Settings (Story, Vision, Engineering) ---
  const [settings, setSettings] = useState({
    storyTitle: "RENGIE Solution Pvt. Ltd.",
    storyDescription: "RENGIE is a purpose-driven solar EPC company focused on accelerating clean energy adoption across Bihar and Jharkhand...",
    visionDescription: "To become Eastern India's most trusted engineering-driven renewable energy company...",
    missionPoint1: "Deliver properly engineered solar systems — not just sell panels",
    missionPoint2: "Make solar accessible, transparent, and hassle-free",
    missionPoint3: "Ensure every customer gets real savings",
    missionPoint4: "Provide lifecycle support with O&M",
    
    engStep1_Title: "Detailed Site Inspection", engStep1_Desc: "Complete roof survey and structural assessment",
    engStep2_Title: "Load Analysis", engStep2_Desc: "Accurate electricity consumption pattern study",
    engStep3_Title: "Shadow Assessment", engStep3_Desc: "Year-round shadow analysis for optimal placement",
    engStep4_Title: "Climate Study", engStep4_Desc: "Design for Bihar-Jharkhand weather conditions"
  });

  // --- 2. State for Leadership Team ---
  const [leaders, setLeaders] = useState([]);
 

  // --- 3. Fetch Data on Load ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Parallel API calls for efficiency
     const [settingsRes, teamRes] = await Promise.all([
  api.get('/About/settings'), // Base URL api.js se lega automatically
  api.get('/About/team')
]);

        if (settingsRes.data && settingsRes.data.storyTitle) {
          setSettings(settingsRes.data);
        }
        
        if (teamRes.data && teamRes.data.length > 0) {
          setLeaders(teamRes.data);
        } else {
            // Fallback if DB is empty (Your original static data)
            setLeaders([
                {
                  name: "Anil Kumar", position: "Founder & Director", education: "NIT Hamirpur & DTU", experience: "8+ years",
                  description: "Hands-on experience in Distributed Renewable Energy projects. Leads technical design.",
                  colorHex: "#1B3F6E", imageUrl: ""
                },
                {
                  name: "Ashish Vishwakarma", position: "Co-Founder", education: "NIT Hamirpur", experience: "16+ years",
                  description: "International experience in construction and infrastructure execution.",
                  colorHex: "#00D084", imageUrl: ""
                },
                {
                  name: "Kumar Jainit", position: "Sales Head", education: "MBA Sales", experience: "13+ years",
                  description: "Expertise in sales leadership and solar market expansion.",
                  colorHex: "#FF9800", imageUrl: ""
                }
            ]);
        }

      } catch (err) {
        console.error("Error fetching About data:", err);
      }
    };

    fetchData();
  }, []);

  // --- Helper Data Structures ---
  const visionMission = [
    {
      icon: <FaEye />,
      title: "Our Vision",
      description: settings.visionDescription,
      color: "#2196F3"
    },
    {
      icon: <FaBullseye />,
      title: "Our Mission",
      points: [
        settings.missionPoint1,
        settings.missionPoint2,
        settings.missionPoint3,
        settings.missionPoint4
      ],
      color: "#00D084"
    }
  ];

  const engineeringApproach = [
    { icon: "🔍", title: settings.engStep1_Title, description: settings.engStep1_Desc },
    { icon: "📊", title: settings.engStep2_Title, description: settings.engStep2_Desc },
    { icon: "🌤️", title: settings.engStep3_Title, description: settings.engStep3_Desc },
    { icon: "🌡️", title: settings.engStep4_Title, description: settings.engStep4_Desc }
  ];

  return (
    <section className="about-section py-5 bg-white" id="about">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-5">
          <span className="badge bg-primary mb-3 px-3 py-2">About RENGIE</span>
          <h2 className="display-5 fw-bold text-dark mb-3">
            Who We Are
          </h2>
          <p className="lead text-muted">
            Engineering excellence meets local expertise
          </p>
        </div>

        {/* Company Story */}
        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">
            <div className="card border-0 shadow-lg">
              <div className="card-body p-5">
                <h3 className="fw-bold mb-4" style={{ color: '#1B3F6E' }}>
                  {settings.storyTitle}
                </h3>
                
                {/* We render description carefully to keep bold tags if stored as plain text, 
                    or simply as text. For complex HTML from DB, use dangerouslySetInnerHTML safely. 
                    Here assuming plain text for simplicity or basic text. */}
                <p className="lead text-muted mb-4" style={{whiteSpace: 'pre-line'}}>
                  {settings.storyDescription}
                </p>
                
                <div 
                  className="alert mb-4" 
                  style={{ 
                    background: 'linear-gradient(135deg, #E8F8F3 0%, #E3F2FD 100%)',
                    border: '2px solid #00D084',
                    borderRadius: '12px'
                  }}
                >
                  <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                    <i className="bi bi-lightning-charge-fill me-2" style={{ color: '#00D084' }}></i>
                    Our Core Principle
                  </h5>
                  <p className="mb-0 lead" style={{ color: '#1B3F6E' }}>
                    <strong>Solar systems must be engineered, not sold.</strong>
                  </p>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="icon-box me-3" style={{width:'50px', height:'50px', background:'#FF9800', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem'}}>🌡️</div>
                      <div><strong style={{ color: '#1B3F6E' }}>High Temperatures</strong><p className="mb-0 small text-muted">Summer heat optimization</p></div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="icon-box me-3" style={{width:'50px', height:'50px', background:'#9E9E9E', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem'}}>💨</div>
                      <div><strong style={{ color: '#1B3F6E' }}>Dust & Pollution</strong><p className="mb-0 small text-muted">Self-cleaning design</p></div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="icon-box me-3" style={{width:'50px', height:'50px', background:'#2196F3', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem'}}>🌧️</div>
                      <div><strong style={{ color: '#1B3F6E' }}>Monsoon Impact</strong><p className="mb-0 small text-muted">Waterproofing & humidity</p></div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="icon-box me-3" style={{width:'50px', height:'50px', background:'#F44336', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem'}}>⚡</div>
                      <div><strong style={{ color: '#1B3F6E' }}>Grid Fluctuations</strong><p className="mb-0 small text-muted">Voltage stabilization</p></div>
                    </div>
                  </div>
                </div>

                <p className="text-muted mb-0">
                  Empanelled with <strong>SBPDCL, NBPDCL, and JBVNL</strong>, RENGIE specializes in residential and commercial solar. We are a <strong>DPIIT-recognized startup</strong> committed to technical excellence.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Engineering Approach */}
        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">
            <div className="text-center mb-4">
              <h4 className="fw-bold" style={{ color: '#1B3F6E' }}>
                <FaCheckCircle className="me-2" style={{ color: '#00D084' }} />
                Our Engineering-First Approach
              </h4>
              <p className="text-muted">Every project begins with comprehensive technical analysis</p>
            </div>
            <div className="row g-4">
              {engineeringApproach.map((item, index) => (
                <div className="col-md-6" key={index}>
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body p-4 d-flex align-items-start">
                      <div className="me-3" style={{ fontSize: '2.5rem' }}>{item.icon}</div>
                      <div>
                        <h6 className="fw-bold mb-2" style={{ color: '#1B3F6E' }}>{item.title}</h6>
                        <p className="text-muted small mb-0">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="row g-4 mb-5">
          {visionMission.map((item, index) => (
            <div className="col-lg-6" key={index}>
              <div 
                className="card border-0 shadow-lg h-100"
                style={{ transition: 'all 0.3s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-10px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)'; }}
              >
                <div className="card-body p-5">
                  <div className="icon-wrapper mb-4" style={{width:'80px', height:'80px', background:item.color, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.5rem', color:'#ffffff', boxShadow:`0 8px 25px ${item.color}40`}}>
                    {item.icon}
                  </div>
                  <h3 className="fw-bold mb-4" style={{ color: '#1B3F6E' }}>{item.title}</h3>
                  {item.description ? (
                    <p className="text-muted mb-0" style={{ lineHeight: '1.8' }}>{item.description}</p>
                  ) : (
                    <ul className="list-unstyled mb-0">
                      {item.points.map((point, idx) => (
                        <li className="mb-3 d-flex align-items-start" key={idx}>
                          <i className="bi bi-check-circle-fill me-3 mt-1" style={{ color: item.color, fontSize: '1.2rem' }}></i>
                          <span className="text-muted" style={{ lineHeight: '1.7' }}>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Leadership Team Section (Dynamic) */}
       {/* Leadership Team Section */}
        <div className="leadership-section mt-5">
          <div className="text-center mb-5">
            <h3 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
              <FaUserTie className="me-2" style={{ color: '#00D084' }} />
              Our Leadership Team
            </h3>
            <p className="text-muted">
              Experienced engineers and business leaders driving solar adoption
            </p>
          </div>

          <div className="row g-4">
            {leaders.map((leader, index) => (
              <div className="col-lg-4 col-md-6" key={index}>
                <div 
                  className="leader-card card border-0 shadow-lg h-100 overflow-hidden"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-10px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  
                  {/* Avatar Section */}
                  <div 
                    className="leader-header text-center pt-5 pb-4"
                    style={{ background: `linear-gradient(135deg, ${leader.colorHex} 0%, ${leader.colorHex}dd 100%)` }}
                  >
                    <div 
                      className="leader-avatar mx-auto mb-3"
                      style={{
                        width: '120px', height: '120px', background: '#ffffff', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '3rem', fontWeight: 'bold', color: leader.colorHex,
                        border: '4px solid rgba(255, 255, 255, 0.3)', overflow: 'hidden'
                      }}
                    >
                      {/* 👇 IMAGE URL FIX: Check if full URL or relative path */}
                      {leader.imageUrl ? (
                        <img 
                          src={leader.imageUrl.startsWith('http') ? leader.imageUrl : `${config.IMG_BASE_URL}${leader.imageUrl}`}
                          alt={leader.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        leader.name.charAt(0)
                      )}
                    </div>
                  </div>

                  {/* Info Section */}
                  <div className="card-body p-4 text-center">
                    <h4 className="fw-bold mb-2" style={{ color: '#1B3F6E' }}>{leader.name}</h4>
                    <p className="fw-semibold mb-3" style={{ color: leader.colorHex }}>{leader.position}</p>
                    
                    <div className="leader-details mb-3">
                      <div className="mb-2"><small className="text-muted">{leader.education}</small></div>
                      <div className="mb-3"><small className="fw-semibold" style={{ color: leader.colorHex }}>{leader.experience}</small></div>
                    </div>

                    <p className="text-muted small mb-4">{leader.description}</p>

                    {/* 👇 LINKEDIN BUTTON FIX */}
                    {leader.linkedInUrl && (
                      <a 
                        href={leader.linkedInUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn btn-outline-primary btn-sm rounded-pill"
                      >
                        <FaLinkedin className="me-2" /> Connect on LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Credentials Badge (Static Footer) */}
        <div className="row mt-5">
          <div className="col-lg-10 mx-auto">
            <div className="card border-0 shadow-lg text-center p-4" style={{ background: 'linear-gradient(135deg, #E8F8F3 0%, #E3F2FD 100%)' }}>
              <div className="card-body">
                <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                  <i className="bi bi-patch-check-fill text-success me-2"></i>
                  Officially Recognized & Empanelled
                </h5>
                <div className="d-flex justify-content-center gap-4 flex-wrap">
                  <span className="badge bg-success px-4 py-2"><i className="bi bi-check-circle-fill me-2"></i>SBPDCL Empanelled</span>
                  <span className="badge bg-success px-4 py-2"><i className="bi bi-check-circle-fill me-2"></i>NBPDCL Empanelled</span>
                  <span className="badge bg-success px-4 py-2"><i className="bi bi-check-circle-fill me-2"></i>JBVNL Empanelled</span>
                  <span className="badge bg-primary px-4 py-2"><i className="bi bi-award-fill me-2"></i>DPIIT Recognized Startup</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;